import { type Resource, type ResourceInput, resourceInputSchema, routes } from '@pmai/contracts';
import { FormDialog, TextAreaField, TextField } from '@pmai/ui';
import { useTranslation } from 'react-i18next';
import { useApiQuery } from '../../shared/api/hooks';
import { usePermissions } from '../../shared/auth/session';
import { bind } from '../../shared/forms/form';
import { useEntityForm } from '../../shared/forms/use-entity-form';

const empty: ResourceInput = {
  name: '',
  role: '',
  availability: '',
  hourlyRate: '',
  competences: '',
  jiraAccountId: '',
};

const valuesOf = (r: Resource): ResourceInput => ({
  name: r.name,
  role: r.role,
  availability: r.availability === null ? '' : String(r.availability),
  hourlyRate: r.hourlyRate ?? '',
  competences: r.competences ?? '',
  jiraAccountId: r.jiraAccountId ?? '',
});

/** "Jauns resurss" / "Rediģēt resursu" (FR-RES-002/003). */
export function ResourceDialog({
  open,
  resource,
  onClose,
}: {
  open: boolean;
  resource: Resource | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const canSeeRate = usePermissions().includes('finance.view');
  // Authors of loaded Jira worklogs (FR-RES-005).
  const authors = useApiQuery(routes.jira.authors, {}, { enabled: open });
  const { form, create, update, dialog } = useEntityForm({
    schema: resourceInputSchema,
    open,
    record: resource,
    values: resource ? valuesOf(resource) : empty,
    create: routes.resources.create,
    update: routes.resources.update,
    invalidates: [routes.resources.list],
    onClose,
  });

  return (
    <FormDialog
      {...dialog}
      title={resource ? t('resources.editTitle') : t('resources.new')}
      onSubmit={form.handleSubmit((body) =>
        resource
          ? update.mutate({ params: { id: resource.id }, body: { ...body, version: resource.version } })
          : create.mutate({ body }),
      )}
    >
      <TextField label={t('resources.fields.name')} required {...bind(form, 'name')} />
      <TextField label={t('resources.fields.role')} required {...bind(form, 'role')} />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          label={t('resources.fields.availability')}
          inputMode="numeric"
          {...bind(form, 'availability')}
        />
        {canSeeRate && (
          <TextField
            label={t('resources.fields.hourlyRate')}
            inputMode="decimal"
            {...bind(form, 'hourlyRate')}
          />
        )}
      </div>
      <TextAreaField label={t('resources.fields.competences')} rows={2} {...bind(form, 'competences')} />
      <TextField
        label={t('resources.fields.jiraAccountId')}
        hint={t('resources.jiraAccountHint')}
        suggestions={(authors.data ?? []).map((a) => ({ value: a.accountId, label: a.name }))}
        {...bind(form, 'jiraAccountId')}
      />
    </FormDialog>
  );
}
